// ChecklistService.js

// Firebase
import { collection, deleteDoc, doc, getDoc, getDocs, onSnapshot, query, setDoc, Timestamp, updateDoc, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Utilities
import { generateKey } from 'utilities/Keys';

// Utilities
import {
  buildSeedItemsFromChecklistField,
  normalizeChecklistTemplateItems,
  syncChecklistItemsWithTemplate,
} from 'utilities/ChecklistTemplateItems';

// Services
import {
  computeChecklistProgress,
  createChecklistSnapshotItems,
  deriveChecklistAssignmentKeys,
  deriveChecklistStatus,
  normalizeChecklistAnchor,
} from './checklistModels';

const CHECKLIST_RECORDS_COLLECTION = collections.checklistRecords;

const normalizeString = (value) => String(value || '').trim();

const getActorKey = (actor, fallbackUserKey = '') => {
  return normalizeString(
    actor?.key
    || actor?.userKey
    || actor?.uid
    || actor?.data_profile?.key
    || fallbackUserKey
  );
};

const getActorName = (actor) => {
  return normalizeString(
    actor?.data_profile?.name
    || actor?.name
    || actor?.title
    || actor?.displayName
    || actor?.email
  );
};

const buildActorSummary = (actor, fallbackUserKey = '') => {
  const actorKey = getActorKey(actor, fallbackUserKey);
  const actorName = getActorName(actor);

  if (!actorKey && !actorName) return null;

  return {
    key: actorKey || null,
    userKey: actorKey || null,
    title: actorName || actorKey || null,
    data_profile: {
      name: actorName || actorKey || null,
      email: normalizeString(actor?.email || actor?.data_profile?.email) || null,
    },
    coverUrl: normalizeString(actor?.coverUrl) || null,
  };
};

function normalizeChecklistRow(row = {}) {
  const sourceSnapshot = row?.sourceSnapshot && typeof row.sourceSnapshot === 'object'
    ? row.sourceSnapshot
    : {};
  const snapshotItems = createChecklistSnapshotItems(sourceSnapshot?.items || sourceSnapshot?.sections || []);
  const assignmentKeys = deriveChecklistAssignmentKeys(snapshotItems);

  return {
    ...row,
    ...assignmentKeys,
    sourceSnapshot: {
      ...sourceSnapshot,
      items: snapshotItems,
    },
  };
}

function normalizeTitle(title, anchor, fieldLabel = '') {
  const trimmedTitle = normalizeString(title);
  if (trimmedTitle) return trimmedTitle;
  const fromField = normalizeString(fieldLabel);
  if (fromField) return fromField;
  const anchorLabel = normalizeString(anchor?.label);
  if (anchorLabel) return anchorLabel;
  return 'Checklist';
}

function pickCanonicalChecklist(rows = []) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  if (!list.length) return null;
  if (list.length === 1) return list[0];

  // Prefer the oldest list so legacy multi-run fields stabilize on one document.
  return [...list].sort((left, right) => {
    const leftCreated = left?.createdAt?.seconds || left?.createdAt?._seconds || 0;
    const rightCreated = right?.createdAt?.seconds || right?.createdAt?._seconds || 0;
    if (leftCreated !== rightCreated) return leftCreated - rightCreated;
    return String(left?.key || '').localeCompare(String(right?.key || ''));
  })[0];
}

function buildChecklistProgressSummary(progress = {}, items = []) {
  const sourceItems = Array.isArray(items) ? items : [];
  const itemTexts = sourceItems
    .map((item) => normalizeString(item?.text || item))
    .filter(Boolean);

  const completedKeys = sourceItems
    .filter((item) => String(item?.status || '').toLowerCase() === 'done')
    .map((item) => normalizeString(item?.templateKey || item?.key))
    .filter(Boolean)
    .sort((left, right) => left.localeCompare(right));

  const totalItems = Number(progress?.totalItems);
  const completedItems = Number(progress?.completedItems);
  const resolvedTotal = Number.isFinite(totalItems) && totalItems > 0
    ? totalItems
    : itemTexts.length;
  const resolvedCompleted = Number.isFinite(completedItems)
    ? completedItems
    : completedKeys.length;

  return {
    kind: 'checklist',
    totalItems: resolvedTotal,
    completedItems: resolvedCompleted,
    itemTexts,
    completedKeys,
  };
}

function buildChecklistPayload({
  key,
  baseKey,
  userKey,
  actor = null,
  title,
  fieldLabel = '',
  anchor,
  items = [],
  sections = [],
  status,
} = {}) {
  const trimmedBaseKey = normalizeString(baseKey);
  const trimmedUserKey = normalizeString(userKey);
  const normalizedAnchor = normalizeChecklistAnchor(anchor);

  if (!trimmedBaseKey || !trimmedUserKey || !normalizedAnchor) return null;

  const snapshotItems = createChecklistSnapshotItems(Array.isArray(items) && items.length > 0 ? items : sections);
  const assignmentKeys = deriveChecklistAssignmentKeys(snapshotItems);
  const progress = computeChecklistProgress(snapshotItems);
  const now = Timestamp.now();
  const checklistKey = normalizeString(key) || generateKey();
  const normalizedTitle = normalizeTitle(title, normalizedAnchor, fieldLabel);
  const derivedStatus = deriveChecklistStatus(progress, status);
  const actorSummary = buildActorSummary(actor, trimmedUserKey);
  const updatedBy = getActorKey(actor, trimmedUserKey) || trimmedUserKey || null;

  return {
    key: checklistKey,
    baseKey: trimmedBaseKey,
    templateKey: null,
    templateRevision: null,
    title: normalizedTitle,
    status: derivedStatus,
    anchor: normalizedAnchor,
    createdBy: trimmedUserKey,
    updatedBy,
    updatedByUser: actorSummary,
    createdAt: now,
    updatedAt: now,
    startedAt: now,
    completedAt: derivedStatus === 'completed' ? now : null,
    progress,
    ...assignmentKeys,
    sourceSnapshot: {
      templateKey: null,
      templateRevision: null,
      items: snapshotItems,
    },
  };
}

export async function createChecklist(options = {}) {
  const payload = buildChecklistPayload(options);
  if (!payload) return null;

  await setDoc(doc(db, CHECKLIST_RECORDS_COLLECTION, payload.key), payload, { merge: false });

  return payload;
}

/**
 * Ensures exactly one checklist document exists for a node field (v2: one field = one list).
 * When `field` includes checklistItems, seeds / syncs type-level template items (v3).
 * If legacy multi-run docs exist, returns the canonical (oldest) without deleting extras.
 */
export async function ensureChecklistForField({
  baseKey,
  userKey,
  actor = null,
  nodeKey,
  nodeType = null,
  nodeLabel = '',
  fieldName,
  fieldLabel = '',
  field = null,
  items = null,
} = {}) {
  const trimmedBaseKey = normalizeString(baseKey);
  const trimmedUserKey = normalizeString(userKey);
  const trimmedNodeKey = normalizeString(nodeKey);
  const trimmedFieldName = normalizeString(fieldName);

  if (!trimmedBaseKey || !trimmedUserKey || !trimmedNodeKey || !trimmedFieldName) {
    return null;
  }

  const schemaField = field && typeof field === 'object' ? field : null;
  const templateItems = normalizeChecklistTemplateItems(schemaField?.checklistItems);
  const seedFromTemplate = buildSeedItemsFromChecklistField(schemaField);

  const existing = await listChecklistsForNodeField({
    baseKey: trimmedBaseKey,
    nodeKey: trimmedNodeKey,
    fieldName: trimmedFieldName,
  });
  const canonical = pickCanonicalChecklist(existing);
  if (canonical) {
    if (Array.isArray(items) && items.length > 0) {
      const nextItems = createChecklistSnapshotItems(items);
      await updateChecklist(canonical.key, {
        title: normalizeString(canonical.title) || fieldLabel || 'Checklist',
        sourceSnapshot: {
          templateKey: null,
          templateRevision: null,
          items: nextItems,
        },
      }, { actor: actor || { key: trimmedUserKey } });
      return getChecklist(canonical.key);
    }

    if (schemaField && templateItems.length > 0) {
      const currentItems = Array.isArray(canonical?.sourceSnapshot?.items)
        ? canonical.sourceSnapshot.items
        : [];
      const { items: syncedItems, changed } = syncChecklistItemsWithTemplate({
        existingItems: currentItems,
        field: schemaField,
      });
      if (changed) {
        await updateChecklist(canonical.key, {
          title: normalizeString(canonical.title) || fieldLabel || 'Checklist',
          sourceSnapshot: {
            templateKey: null,
            templateRevision: null,
            items: syncedItems,
          },
        }, { actor: actor || { key: trimmedUserKey } });
        return getChecklist(canonical.key);
      }
    }

    return canonical;
  }

  const createItems = Array.isArray(items) && items.length > 0
    ? items
    : seedFromTemplate;

  return createChecklist({
    baseKey: trimmedBaseKey,
    userKey: trimmedUserKey,
    actor,
    title: fieldLabel || 'Checklist',
    fieldLabel,
    anchor: {
      kind: 'node',
      nodeKey: trimmedNodeKey,
      nodeType,
      fieldName: trimmedFieldName,
      label: nodeLabel || fieldLabel || trimmedNodeKey,
    },
    items: createItems,
  });
}

export async function listChecklistsForNodeField({ baseKey, nodeKey, fieldName } = {}) {
  const trimmedBaseKey = normalizeString(baseKey);
  const trimmedNodeKey = normalizeString(nodeKey);
  const trimmedFieldName = normalizeString(fieldName);

  if (!trimmedBaseKey || !trimmedNodeKey || !trimmedFieldName) {
    return [];
  }

  const q = query(
    collection(db, CHECKLIST_RECORDS_COLLECTION),
    where('baseKey', '==', trimmedBaseKey),
    where('anchor.kind', '==', 'node'),
    where('anchor.nodeKey', '==', trimmedNodeKey),
    where('anchor.fieldName', '==', trimmedFieldName),
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => (
    normalizeChecklistRow({ id: docSnap.id, ...(docSnap.data() || {}), key: docSnap.id })
  ));
}

export async function updateChecklist(checklistKey, data = {}, options = {}) {
  const trimmedChecklistKey = normalizeString(checklistKey);
  if (!trimmedChecklistKey) return;

  const beforeChecklist = await getChecklist(trimmedChecklistKey);
  if (!beforeChecklist) return;

  const actorSummary = buildActorSummary(options?.actor || null, beforeChecklist?.updatedBy || beforeChecklist?.createdBy || '');
  const actorKey = getActorKey(options?.actor || null, beforeChecklist?.updatedBy || beforeChecklist?.createdBy || '');

  const payload = {
    ...data,
    updatedAt: Timestamp.now(),
    updatedBy: actorKey || beforeChecklist?.updatedBy || beforeChecklist?.createdBy || null,
    updatedByUser: actorSummary || beforeChecklist?.updatedByUser || null,
  };

  if (Object.prototype.hasOwnProperty.call(data, 'title')) {
    payload.title = normalizeString(data.title) || 'Checklist';
  }

  if (Object.prototype.hasOwnProperty.call(data, 'sourceSnapshot')) {
    const nextItems = createChecklistSnapshotItems(data?.sourceSnapshot?.items || data?.sourceSnapshot?.sections || []);
    const assignmentKeys = deriveChecklistAssignmentKeys(nextItems);
    payload.sourceSnapshot = {
      templateKey: null,
      templateRevision: null,
      items: nextItems,
    };
    payload.progress = computeChecklistProgress(nextItems);
    payload.status = deriveChecklistStatus(payload.progress, data?.status);
    payload.completedAt = payload.status === 'completed' ? Timestamp.now() : null;
    payload.assigneeKeys = assignmentKeys.assigneeKeys;
    payload.openAssigneeKeys = assignmentKeys.openAssigneeKeys;
  }

  if (!Object.prototype.hasOwnProperty.call(payload, 'status') && Object.prototype.hasOwnProperty.call(payload, 'progress')) {
    payload.status = deriveChecklistStatus(payload.progress);
  }

  await updateDoc(doc(db, CHECKLIST_RECORDS_COLLECTION, trimmedChecklistKey), payload);

}

export async function deleteChecklist(checklistKey) {
  const trimmedChecklistKey = normalizeString(checklistKey);
  if (!trimmedChecklistKey) return;

  const checklist = await getChecklist(trimmedChecklistKey);
  if (!checklist) return;

  await deleteDoc(doc(db, CHECKLIST_RECORDS_COLLECTION, trimmedChecklistKey));
}

export async function getChecklist(checklistKey) {
  const trimmedChecklistKey = normalizeString(checklistKey);
  if (!trimmedChecklistKey) return null;

  try {
    const ref = doc(db, CHECKLIST_RECORDS_COLLECTION, trimmedChecklistKey);
    const snapshot = await getDoc(ref);
    if (!snapshot.exists()) return null;
    return normalizeChecklistRow({ id: snapshot.id, ...(snapshot.data() || {}), key: snapshot.id });
  } catch (error) {
    console.error('[ChecklistService] getChecklist error:', error);
    throw error;
  }
}

export function subscribeToChecklist(checklistKey, callback) {
  if (typeof callback !== 'function') {
    return () => {};
  }

  const trimmedChecklistKey = normalizeString(checklistKey);
  if (!trimmedChecklistKey) {
    callback(null);
    return () => {};
  }

  return onSnapshot(
    doc(db, CHECKLIST_RECORDS_COLLECTION, trimmedChecklistKey),
    (docSnap) => {
      const row = docSnap.exists()
        ? normalizeChecklistRow({ id: docSnap.id, ...(docSnap.data() || {}), key: docSnap.id })
        : null;
      callback(row);
    },
    (error) => {
      console.error('[ChecklistService] subscribeToChecklist error:', error);
      callback(null);
    }
  );
}

export function subscribeToChecklistsForNode({ baseKey, nodeKey, fieldName } = {}, callback) {
  if (typeof callback !== 'function') {
    return () => {};
  }

  const trimmedBaseKey = normalizeString(baseKey);
  const trimmedNodeKey = normalizeString(nodeKey);
  const trimmedFieldName = normalizeString(fieldName);

  if (!trimmedBaseKey || !trimmedNodeKey) {
    callback([]);
    return () => {};
  }

  const constraints = [
    where('baseKey', '==', trimmedBaseKey),
    where('anchor.kind', '==', 'node'),
    where('anchor.nodeKey', '==', trimmedNodeKey),
  ];

  if (trimmedFieldName) {
    constraints.push(where('anchor.fieldName', '==', trimmedFieldName));
  }

  const q = query(collection(db, CHECKLIST_RECORDS_COLLECTION), ...constraints);

  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs
        .map((docSnap) => normalizeChecklistRow({ id: docSnap.id, ...(docSnap.data() || {}), key: docSnap.id }))
        .sort((a, b) => {
          const aTime = a?.updatedAt?.seconds || 0;
          const bTime = b?.updatedAt?.seconds || 0;
          return bTime - aTime;
        });

      callback(rows);
    },
    (error) => {
      console.error('[ChecklistService] subscribeToChecklistsForNode error:', error);
      callback([]);
    }
  );
}

/** v2: subscribe to the single canonical checklist for a node field. */
export function subscribeToChecklistForField({ baseKey, nodeKey, fieldName } = {}, callback) {
  return subscribeToChecklistsForNode({ baseKey, nodeKey, fieldName }, (rows) => {
    callback(pickCanonicalChecklist(rows));
  });
}

export { buildChecklistProgressSummary, pickCanonicalChecklist };

export function subscribeToAssignedChecklistsForMember({ baseKey, memberKey } = {}, callback) {
  if (typeof callback !== 'function') {
    return () => {};
  }

  const trimmedBaseKey = normalizeString(baseKey);
  const trimmedMemberKey = normalizeString(memberKey);

  if (!trimmedBaseKey || !trimmedMemberKey) {
    callback([]);
    return () => {};
  }

  const q = query(
    collection(db, CHECKLIST_RECORDS_COLLECTION),
    where('baseKey', '==', trimmedBaseKey),
    where('assigneeKeys', 'array-contains', trimmedMemberKey),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs
        .map((docSnap) => normalizeChecklistRow({ id: docSnap.id, ...(docSnap.data() || {}), key: docSnap.id }))
        .sort((a, b) => {
          const aTime = a?.updatedAt?.seconds || 0;
          const bTime = b?.updatedAt?.seconds || 0;
          return bTime - aTime;
        });

      callback(rows);
    },
    (error) => {
      console.error('[ChecklistService] subscribeToAssignedChecklistsForMember error:', error);
      callback([]);
    }
  );
}

export async function toggleChecklistItemStatus({ checklistKey, itemKey, actor = null } = {}) {
  const trimmedChecklistKey = normalizeString(checklistKey);
  const trimmedItemKey = normalizeString(itemKey);

  if (!trimmedChecklistKey || !trimmedItemKey) return null;

  const checklist = await getChecklist(trimmedChecklistKey);
  if (!checklist) return null;

  const currentItems = Array.isArray(checklist?.sourceSnapshot?.items)
    ? checklist.sourceSnapshot.items
    : [];

  let didUpdateItem = false;
  const nextItems = currentItems.map((item) => {
    if (normalizeString(item?.key) !== trimmedItemKey) {
      return item;
    }

    didUpdateItem = true;

    return {
      ...item,
      status: String(item?.status || '').trim().toLowerCase() === 'done' ? 'todo' : 'done',
      completedAt: String(item?.status || '').trim().toLowerCase() === 'done' ? null : Timestamp.now(),
    };
  });

  if (!didUpdateItem) {
    return checklist;
  }

  await updateChecklist(trimmedChecklistKey, {
    title: checklist?.title || 'Checklist',
    sourceSnapshot: {
      templateKey: null,
      templateRevision: null,
      items: nextItems,
    },
  }, { actor });

  return getChecklist(trimmedChecklistKey);
}